import { Book, CalendarClock, Home, Plus, User } from 'lucide-react'
import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'

import { useT, type StringId } from '../lib/i18n'
import { OverlayContext } from './OverlayContext'
import { PhoneFrame } from './PhoneFrame'
import { HeaderSlotContext } from './ShellHeader'

const TABS = [
  { to: '/today', labelKey: 'nav.today', Icon: Home },
  { to: '/diary', labelKey: 'nav.diary', Icon: Book },
  { to: '/fasting', labelKey: 'nav.fasting', Icon: CalendarClock },
  { to: '/me', labelKey: 'nav.me', Icon: User },
] as const satisfies readonly { to: string; labelKey: StringId; Icon: typeof Home }[]

/**
 * The shell the tab routes render inside.
 *
 * ```
 * PhoneFrame                     relative, the 430px column
 * ├── header slot                shrink-0, empty:hidden — filled by <ShellHeader>
 * ├── <main>                     flex-1 overflow-y-auto — the ONLY scroller
 * ├── TabBar                     in flow at the end of the column, never fixed
 * └── #overlay-root              absolute inset-0, inside the column
 * ```
 *
 * `min-h-0` on `<main>` is belt-and-braces, not the mechanism: `overflow-y-auto`
 * already gives the flex item an automatic minimum size of 0 (CSS Box Sizing
 * § 4.1), and a review confirmed the layout holds without it. It stays because
 * the day someone drops the overflow class is the day the bar leaves the screen,
 * and one defensive class is cheaper than that bug.
 *
 * SUAP-0104.
 */
export function AppShell() {
  const { t } = useT()
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null)
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null)
  const navigate = useNavigate()

  return (
    <PhoneFrame>
      <HeaderSlotContext.Provider value={headerSlot}>
      <OverlayContext.Provider value={overlayRoot}>
        {/* The header slot sits ABOVE <main>, which is the scroll container —
            a header rendered inside <main> scrolls away with the content. A
            screen fills this by rendering <ShellHeader>; one that renders none
            collapses the slot to nothing.

            It is a <header> rather than a <div> because that placement left the
            screen title outside every landmark, which is what axe's `region`
            rule reported against this element's own class list. `Pushed` already
            writes <header> for the same job one route away, so the two columns
            now say it the same way. `empty:hidden` is display:none, so a screen
            with no ShellHeader contributes no empty landmark. SUAP-0113. */}
        <header ref={setHeaderSlot} className="shrink-0 empty:hidden" />

        {/* --suap-safe-b is zeroed for the tab subtree: the bar below already
            reserves the real inset, so a screen inside the tabs needs to know
            nothing about it. A pushed route or a sheet renders outside <main>
            and reads the true value from :root. */}
        <main
          className="min-h-0 flex-1 overflow-y-auto"
          style={{ ['--suap-safe-b' as string]: '0px' }}
        >
          <Outlet />
        </main>

        <nav
          aria-label={t('nav.main')}
          className="relative flex shrink-0 items-stretch border-t border-hairline bg-surface"
          style={{
            height: 'calc(64px + var(--suap-safe-b))',
            paddingBottom: 'var(--suap-safe-b)',
          }}
        >
          {TABS.slice(0, 2).map((tab) => (
            <Tab key={tab.to} {...tab} />
          ))}

          {/* The Log action straddles the bar. Straddling without position:
              fixed — the bar is relative, the button is absolute inside it.

              The ring is drawn INSIDE the coral, in on-accent. Its fill is the
              accent, so the pair that has to clear 3:1 is ring-against-accent,
              not ring-against-surface — and the browser default it used to fall
              back to was its own blue on that coral: 1.94:1 light, 1.77:1 dark.
              `--color-on-accent` is the pair the design system grades at 5.38:1.
              SUAP-0113. */}
          <button
            type="button"
            onClick={() => navigate('/log')}
            aria-label={t('nav.log')}
            className="absolute -top-5 left-1/2 flex h-14 w-14 -translate-x-1/2 items-center justify-center rounded-chip bg-accent text-on-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-on-accent"
          >
            <Plus aria-hidden size={26} />
          </button>
          <div className="w-14 shrink-0" aria-hidden />

          {TABS.slice(2).map((tab) => (
            <Tab key={tab.to} {...tab} />
          ))}
        </nav>

        {/* Inside the column, so a scrim darkens 430px and not 1920. */}
        <div ref={setOverlayRoot} id="overlay-root" className="absolute inset-0 empty:hidden" />
      </OverlayContext.Provider>
      </HeaderSlotContext.Provider>
    </PhoneFrame>
  )
}

interface TabProps {
  to: string
  labelKey: StringId
  Icon: typeof Home
}

/**
 * A tab. `NavLink` supplies aria-current="page", which is also how tab state is asserted.
 *
 * The ring is explicit and **inset**. Explicit because the browser default is
 * 1px in its own blue, which is neither a token nor the 2px the rest of the kit
 * draws — the app's primary navigation was the one place a keyboard user could
 * not see where they were. Inset because a tab is flush to the bar's top hairline
 * and to its neighbours, and a ring drawn outward crosses that border and reads
 * as a broken one. SUAP-0113.
 */
function Tab({ to, labelKey, Icon }: TabProps) {
  const { t } = useT()
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'flex min-h-11 flex-1 flex-col items-center justify-center gap-1 text-xs',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink',
          isActive ? 'text-ink' : 'text-ink-soft',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <Icon aria-hidden size={22} strokeWidth={isActive ? 2.4 : 1.8} />
          <span>{t(labelKey)}</span>
        </>
      )}
    </NavLink>
  )
}
